var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect, useCallback, } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, Divider, styled } from "@mui/material";
import { Card, Form, Icon, IconName, Stack, TextInput, TextInputWidth, } from "@eightfold.ai/octuple";
import Button from "../Button/Button";
import Dialog from "../Dialog/Dialog";
import FormRequired from "../FormRequired/FormRequired";
import { getClientId, useGetAvailableNineBoxes, deleteNineBoxQuery, createNineBoxQuery, useGetDataFiltersQuery, useGetLookupsFiltersQuery, useGetNineBoxLeaderCountWithFilters, useGetUserId, } from "../../store/api/core";
import SpinnerAndErrorer from "../SpinnerAndErrorer";
import { useNavigate } from "../../hooks/navigate";
import { getRoute } from "../../routes/getAllRoutes";
import trStyles from "./TalentReview.module.scss";
import ResetButton from "../ResetButton";
import { useClient } from "../../hooks/client";
import styles from "./TalentReview.module.scss";
import NineBoxErrorMessage from "./NineBoxErrorMessage";
import Tooltip from "../Tooltip";
import { TooltipPlacement } from "../Tooltip/Tooltip";
import { AutoCompleteFilter, updateSelector, useKeyedOptions, } from "../Filters/AutoCompleteFilter";
import EmptyIcon from "../../icons/EmptyIcon";
import { AvailableFilters, dataFilters, getEntity } from "../../common/filters";
import DeleteReportIcon from "../DeleteReportIcon/DeleteReportIcon";
import { TestTag, getAutomationTag } from "../../common/testAutomation";
var Container = styled(Box)({
    padding: "32px",
});
var MainTitle = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        fontFamily: theme.typography.fontFamily + " Heavy",
        fontSize: "20px",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "28px",
    });
});
var SecondContainer = styled(Box)({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "32px",
});
var DescriptionText = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        fontFamily: theme.typography.fontFamily + " Book",
        color: "var(--grey-color-70)",
        fontWeight: "400",
        fontSize: "14px",
        LineHeight: "20px",
    });
});
var SettingIconContainer = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: "16px",
});
var SecondPartContainer = styled(Box)({
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
});
var ImageWrapper = styled(Box)({
    height: "135px",
    margin: "32px 0",
});
var CreateFirstBoxText = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        fontFamily: theme.typography.fontFamily + " Heavy",
        color: "var(--grey-color-90)",
        fontWeight: "400",
        fontSize: "20px",
        lineHeight: "28px",
    });
});
export var useEditableSelectionsMethods = function (_a) {
    var _b, _c;
    var setRerenderKey = _a.setRerenderKey, setSelections = _a.setSelections, selections = _a.selections, disableSave = _a.disableSave;
    var client = useClient();
    var appliedFilters = useMemo(function () {
        return Object.entries(selections || {}).reduce(function (current, _a) {
            var _b;
            var key = _a[0], value = _a[1];
            return (__assign(__assign({}, current), (_b = {}, _b[key] = (value || []).map(function (_a) {
                var value = _a.value;
                return value;
            }), _b)));
        }, {});
    }, [selections]);
    var leaderCountData = useGetNineBoxLeaderCountWithFilters({
        clientId: client.clientId,
        filters: appliedFilters,
    }).data;
    useEffect(function () {
        var _a, _b;
        if (((_a = leaderCountData === null || leaderCountData === void 0 ? void 0 : leaderCountData.data) === null || _a === void 0 ? void 0 : _a.leaderCount) !== undefined) {
            var disabled = ((_b = leaderCountData === null || leaderCountData === void 0 ? void 0 : leaderCountData.data) === null || _b === void 0 ? void 0 : _b.leaderCount) > 1 ? false : true;
            disableSave(disabled);
        }
    }, [(_b = leaderCountData === null || leaderCountData === void 0 ? void 0 : leaderCountData.data) === null || _b === void 0 ? void 0 : _b.leaderCount]);
    var handleReset = function () {
        setSelections(__assign(__assign({}, selections), [
            AvailableFilters.BusinessUnits,
            AvailableFilters.Locations,
            AvailableFilters.LocationCountry,
            AvailableFilters.Division,
            AvailableFilters.BusinessFunctions,
            AvailableFilters.BusinessSubFunctions,
            AvailableFilters.Roles,
            AvailableFilters.JobLevels,
            AvailableFilters.Managers,
        ].reduce(function (current, filter) {
            var _a;
            return (__assign(__assign({}, current), (_a = {}, _a[getEntity(filter)] = [], _a)));
        }, {})));
        setRerenderKey(Math.random());
    };
    return {
        leaderCount: (_c = leaderCountData === null || leaderCountData === void 0 ? void 0 : leaderCountData.data) === null || _c === void 0 ? void 0 : _c.leaderCount,
        handleReset: handleReset,
    };
};
export var EditableNineBoxSelections = function (_a) {
    var isReadOnly = _a.isReadOnly, selections = _a.selections, setSelections = _a.setSelections;
    var client = useClient();
    var update = updateSelector(selections, setSelections);
    var useFilterOptions = useKeyedOptions(useGetDataFiltersQuery);
    var useLookupOptions = function (key) {
        var lookups = useGetLookupsFiltersQuery({ clientId: client.clientId || "" }, { shouldSkipCall: function () { return !client.clientId; } });
        var options = useFilterOptions(key)();
        return function () {
            var _a, _b;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            var lookupCodeMap = ((_b = (_a = lookups === null || lookups === void 0 ? void 0 : lookups.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lookupCodeMap) || {};
            if (!lookupCodeMap) {
                return options;
            }
            return options.map(function (_a) {
                var label = _a.label, value = _a.value;
                return ({
                    value: value,
                    label: lookupCodeMap[value] || label,
                });
            });
        };
    };
    var autoCompleteFilters = [
        {
            filter: AvailableFilters.BusinessUnits,
            useOptions: useFilterOptions,
        },
        {
            filter: AvailableFilters.Locations,
            useOptions: useFilterOptions,
        },
        {
            filter: AvailableFilters.LocationCountry,
            useOptions: useFilterOptions,
        },
        {
            filter: AvailableFilters.Division,
            useOptions: useFilterOptions,
        },
        {
            filter: AvailableFilters.BusinessFunctions,
            useOptions: useLookupOptions,
        },
        {
            filter: AvailableFilters.BusinessSubFunctions,
            useOptions: useLookupOptions,
        },
        {
            filter: AvailableFilters.Roles,
            useOptions: useFilterOptions,
        },
        {
            filter: AvailableFilters.JobLevels,
            useOptions: useLookupOptions,
        },
        {
            filter: AvailableFilters.Managers,
            useOptions: useFilterOptions,
        },
    ];
    return (_jsx(_Fragment, { children: autoCompleteFilters.map(function (_a) {
            var filter = _a.filter, useOptions = _a.useOptions;
            return (_jsx(AutoCompleteFilter, { useOptions: useOptions(getEntity(filter)), selector: update(getEntity(filter)), label: dataFilters[filter].label, disabled: isReadOnly }));
        }) }));
};
var CreateNineBoxDialog = function (props) {
    var form = Form.useForm()[0];
    var createNineBox = createNineBoxQuery({
        invalidates: [{ queryKey: ["{ninebox}/nine-box?clientId={ClientId}"] }],
    });
    var navigate = useNavigate();
    var _a = useState(""), formError = _a[0], setFormError = _a[1];
    var _b = useState(""), errorClass = _b[0], setErrorClass = _b[1];
    var _c = useState(false), disableDoneBtn = _c[0], setDisableDoneBtn = _c[1];
    var changeDoneBtnStatus = function (status) { return setDisableDoneBtn(status); };
    var onDoneClick = function () {
        setFormError("");
        setErrorClass("");
        form.submit();
    };
    var onFormFinish = function (formValues, filters) {
        createNineBox.mutate({
            boxName: formValues.name,
            clientId: getClientId(),
            filters: Object.entries(filters || {}).reduce(function (current, _a) {
                var _b;
                var key = _a[0], value = _a[1];
                return (__assign(__assign({}, current), (_b = {}, _b[key] = (value || []).map(function (_a) {
                    var value = _a.value;
                    return value;
                }), _b)));
            }, {}),
        }, {
            onSuccess: function (data) {
                navigate(getRoute("TalentReviewNineBox").replace(":id", data.data.id));
                props.onClose();
            },
            onError: function (data) {
                var _a;
                if (data.message == "400") {
                    setFormError((_a = data.cause) === null || _a === void 0 ? void 0 : _a.boxName[0]);
                    setErrorClass(trStyles.formError);
                }
            },
        });
    };
    var CreateNineBoxForm = useCallback(function (_a) {
        var changeDoneBtnStatus = _a.changeDoneBtnStatus;
        var _b = useState({}), selections = _b[0], setSelections = _b[1];
        var _c = useState(Math.random()), rerenderKey = _c[0], setRerenderKey = _c[1];
        var _d = useEditableSelectionsMethods({
            selections: selections,
            setSelections: setSelections,
            setRerenderKey: setRerenderKey,
            disableSave: changeDoneBtnStatus,
        }), handleReset = _d.handleReset, leaderCount = _d.leaderCount;
        var handleFormFinish = function (formValues) {
            onFormFinish(formValues, selections);
        };
        return (_jsxs(_Fragment, { children: [_jsx(FormRequired, {}), _jsxs(Form, __assign({ form: form, layout: "vertical", autoComplete: "off", onFinish: handleFormFinish }, { children: [_jsx(Form.Item, __assign({ label: "Name", name: "name", rules: [
                                { required: true, message: "9 Box name is required" },
                                {
                                    type: "string",
                                    max: 30,
                                    message: "9 Box name cannot be more than ${max} characters",
                                },
                            ], classNames: styles.createNineboxNameBox }, { children: _jsx(TextInput, { inputWidth: TextInputWidth.fill, classNames: errorClass }) })), _jsx(EditableNineBoxSelections, { isReadOnly: false, selections: selections, setSelections: setSelections }), createNineBox.isError && (_jsx("div", __assign({ role: "alert", className: "form-item-explain-error ".concat(errorClass) }, { children: formError }))), leaderCount == undefined
                            ? ""
                            : leaderCount <= 1 && _jsx(NineBoxErrorMessage, {})] }), rerenderKey), _jsxs("div", __assign({ className: styles.resetButtonWrapper }, { children: [_jsx(ResetButton, { label: "Reset Filters", onClick: handleReset }), _jsx("div", __assign({ className: styles.filteredLeaderCount }, { children: "".concat(leaderCount ? leaderCount : 0, " ").concat(leaderCount == 1 ? "Leader" : "Leaders") }))] }))] }));
    }, [formError]);
    return (_jsx(Dialog, { header: "Create a new 9 Box", body: _jsx(CreateNineBoxForm, { changeDoneBtnStatus: changeDoneBtnStatus }), buttons: {
            okButtonProps: __assign({ text: "Done", onClick: onDoneClick, loading: createNineBox.isPending, disabled: disableDoneBtn }, getAutomationTag(TestTag.PrimaryActionButtonSubmit)),
            cancelButtonProps: {
                text: "Cancel",
            },
        }, visible: props.visible, onClose: props.onClose, firstFocusableSelector: "input" }));
};
var NoNineBoxes = function (props) { return (_jsxs(SecondPartContainer, { children: [_jsx(ImageWrapper, { children: _jsx(EmptyIcon, {}) }), _jsx(Box, __assign({ sx: { marginBottom: "32px" } }, { children: _jsx(CreateFirstBoxText, { children: "You don't have any 9 Boxes. Create your first one." }) })), _jsx(Box, { children: _jsx(Button, { text: "Create 9 Box", onClick: function () {
                    props.setCreateDialogVisible(true);
                } }) })] })); };
var BoxLine = function (_a) {
    var path = _a.path, description = _a.description, text = _a.text;
    return (_jsxs(Stack, __assign({ direction: "horizontal", flexGap: "xxs", style: {
            color: "var(--grey-color-60)",
            fontFamily: "var(--font-stack-full-medium)",
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "24px",
        } }, { children: [_jsx(Icon, { path: path }), _jsx("div", __assign({ style: { marginLeft: "4px", paddingTop: "4px" } }, { children: description })), _jsx("div", __assign({ style: { color: "var(--grey-color-90)", paddingTop: "4px" } }, { children: text }))] })));
};
var BoxNameLine = function (_a) {
    var name = _a.name, boxId = _a.boxId, actionPermissionsStatus = _a.actionPermissionsStatus;
    var _b = useState(false), visible = _b[0], setVisible = _b[1];
    var deleteBoxId = deleteNineBoxQuery({
        substitutions: { boxId: boxId },
        invalidates: [{ queryKey: ["{ninebox}/nine-box?clientId={ClientId}"] }],
    });
    var handleDeleteBox = function () {
        deleteBoxId.mutate({}, { onSuccess: function () { return setVisible(false); } });
    };
    var props = {
        header: "Are you sure you want to delete this 9 Box? This action cannot be undone.",
        body: "",
        buttons: {
            okButtonProps: {
                text: "Confirm and Delete",
                onClick: handleDeleteBox,
                loading: deleteBoxId.isPending,
            },
            cancelButtonProps: {
                text: "Cancel",
            },
        },
    };
    return (_jsxs(Stack, __assign({ justify: "space-between", fullWidth: true, "data-testid": "nine-box-".concat(boxId) }, { children: [_jsx("div", __assign({ style: {
                    color: "var(--grey-color-90)",
                    fontFamily: "var(--font-stack-full-medium)",
                    fontSize: "20px",
                    lineHeight: "28px",
                    fontWeight: 400,
                } }, { children: name })), actionPermissionsStatus ? (_jsx(Tooltip, __assign({ content: "Requires edit access", className: styles.tooltipSize, placement: TooltipPlacement.Top }, { children: _jsx(DeleteReportIcon, { reportId: boxId, className: styles.disableIcons }) }))) : (_jsx(DeleteReportIcon, { reportId: boxId, onClick: function () { return setVisible(true); }, className: styles.nineBoxDeleteIcon })), visible &&
                createPortal(_jsx(Dialog, __assign({}, props, { visible: visible, onClose: function () { return setVisible(false); } })), document.body)] })));
};
var NineBoxSummary = function (_a) {
    var userFullName = _a.createUser.userFullName, id = _a.id, boxName = _a.boxName, lastUpdatedTs = _a.lastUpdatedTs, boxId = _a.boxId, userPermissions = _a.userPermissions;
    var userId = useGetUserId();
    var actionPermissionsStatus = !(userPermissions === null || userPermissions === void 0 ? void 0 : userPermissions.some(function (user) { return user.userId === userId && user.permissions === "edit"; }));
    var navigate = useNavigate();
    return (_jsx(Card, __assign({ bordered: true, height: 220, style: {
            width: "33%",
            minWidth: "440px",
            padding: "20px 24px",
        } }, { children: _jsxs(Stack, __assign({ direction: "vertical", flexGap: "l", fullWidth: true }, { children: [_jsx(BoxNameLine, { name: boxName, boxId: boxId, actionPermissionsStatus: actionPermissionsStatus }), _jsxs(Stack, __assign({ direction: "vertical", flexGap: "xxs" }, { children: [_jsx(BoxLine, { path: IconName.mdiAccountOutline, description: "Created By:", text: userFullName }), _jsx(BoxLine, { path: IconName.mdiCalendarBlankOutline, description: "Last Edited:", text: lastUpdatedTs === null || lastUpdatedTs === void 0 ? void 0 : lastUpdatedTs.toLocaleString(undefined, {
                                month: "short",
                                day: "2-digit",
                                year: "numeric",
                            }) })] })), _jsx(Button, { text: "View 9 Box", style: {
                        backgroundColor: "#B0F3FE",
                        width: "100px",
                        color: "#054D7B",
                    }, onClick: function () {
                        return navigate(getRoute("TalentReviewNineBox").replace(":id", id));
                    } })] })) })));
};
var NineBoxSummaries = function (_a) {
    var children = _a.children;
    return (_jsx(Stack, __assign({ "data-testid": "NineBoxes", direction: "horizontal", wrap: "wrap", flexGap: "l", style: { marginTop: "32px" }, justify: "flex-start", align: "center" }, { children: children })));
};
var TalentReview = function () {
    var _a, _b;
    var _c = React.useState(false), createDialogVisible = _c[0], setCreateDialogVisible = _c[1];
    var nineBoxes = useGetAvailableNineBoxes({});
    useEffect(function () {
        if (nineBoxes.data == undefined) {
            nineBoxes.refetch();
        }
    }, []);
    var cards = (_b = (((_a = nineBoxes === null || nineBoxes === void 0 ? void 0 : nineBoxes.data) === null || _a === void 0 ? void 0 : _a.data) || [])) === null || _b === void 0 ? void 0 : _b.map(function (box) { return _createElement(NineBoxSummary, __assign({}, box, { key: box.id })); });
    return (_jsxs(Container, __assign({ "data-testid": "TalentReview" }, { children: [_jsx(Box, __assign({ sx: { marginBottom: "8px" } }, { children: _jsx(MainTitle, { children: "Talent Review" }) })), _jsxs(SecondContainer, { children: [_jsx(Box, __assign({ sx: { width: "68%" } }, { children: _jsx(DescriptionText, { children: "Talent review provides a structured, objective process by which to evaluate and generate a holistic view of your talent, enabling informed decisions about development, succession planning, and strategic talent management. To get started, please select an existing talent review or initiate a new review." }) })), _jsxs(SettingIconContainer, { children: [_jsx(Box, { sx: { cursor: "pointer" } }), _jsx(Box, { children: _jsx(Button, { text: "Create new", onClick: function () { return setCreateDialogVisible(true); } }) })] })] }), _jsx(Divider, {}), _jsx(SpinnerAndErrorer, __assign({ conditions: [nineBoxes] }, { children: (cards === null || cards === void 0 ? void 0 : cards.length) == 0 ? (_jsx(NoNineBoxes, { setCreateDialogVisible: setCreateDialogVisible })) : (_jsx(NineBoxSummaries, { children: cards })) })), createDialogVisible &&
                createPortal(_jsx(CreateNineBoxDialog, { visible: createDialogVisible, onClose: function () { return setCreateDialogVisible(false); } }), document.body)] })));
};
export default TalentReview;
